import type {
  AdminUserRow,
  AuditEntry,
  Dashboard,
  DspParent,
  LambdaBinding,
  LambdaDetail,
  LambdaSummary,
  ParentLambdaRow,
  ParentSummary,
  Principal,
  Publication,
  SnowflakeProfile,
  ValidationResult,
} from '../types';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const detail = body?.detail;
    // FastAPI returns either a plain string or a structured object for our
    // 422s; surface the structured message so the caller can render issues.
    const message =
      typeof detail === 'string'
        ? detail
        : (detail?.message ?? `${response.status} ${response.statusText}`);
    throw new ApiError(response.status, message, detail);
  }
  return body as T;
}

export const api = {
  me: () => request<Principal>('/api/auth/me'),
  login: (email: string, password: string, totp_code?: string) =>
    request<Principal & { expires_in: number }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, totp_code: totp_code || null }),
    }),
  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),

  dashboard: () => request<Dashboard>('/api/dashboard'),

  listParents: () => request<ParentSummary[]>('/api/parents'),
  getParent: (key: string) => request<DspParent>(`/api/parents/${key}`),
  createParent: (body: unknown) =>
    request<DspParent>('/api/parents', { method: 'POST', body: JSON.stringify(body) }),
  patchParent: (key: string, body: unknown) =>
    request<DspParent>(`/api/parents/${key}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteParent: (key: string) => request<void>(`/api/parents/${key}`, { method: 'DELETE' }),
  validateParent: (key: string) =>
    request<ValidationResult>(`/api/parents/${key}/validate`, { method: 'POST' }),
  parentLambdas: (key: string) => request<ParentLambdaRow[]>(`/api/parents/${key}/lambda-bindings`),
  putParentBindings: (key: string, bindings: LambdaBinding[]) =>
    request<LambdaBinding[]>(`/api/parents/${key}/lambda-bindings`, {
      method: 'PUT',
      body: JSON.stringify({ bindings }),
    }),
  parentKeys: (key: string, environment: 'prod' | 'qa') =>
    request<{ file_type_key: string; folder: string; direction: string; s3_uri?: string; error?: string }[]>(
      `/api/parents/${key}/keys?environment=${environment}`,
    ),
  previewKey: (key: string, body: unknown) =>
    request<{ s3_uri: string; bucket: string; key: string; folder: string; immutable: boolean }>(
      `/api/parents/${key}/preview-key`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  listLambdas: (params?: {
    ops_area?: 'settlement_operations' | 'lead_scoring' | 'general';
    partner_aware?: boolean;
  }) => {
    const qs = new URLSearchParams();
    if (params?.ops_area) qs.set('ops_area', params.ops_area);
    if (params?.partner_aware !== undefined) {
      qs.set('partner_aware', String(params.partner_aware));
    }
    const suffix = qs.toString() ? `?${qs}` : '';
    return request<LambdaSummary[]>(`/api/lambdas${suffix}`);
  },
  getLambda: (key: string) => request<LambdaDetail>(`/api/lambdas/${key}`),
  patchLambda: (key: string, body: unknown) =>
    request<unknown>(`/api/lambdas/${key}`, { method: 'PATCH', body: JSON.stringify(body) }),
  putLambdaBindings: (key: string, bindings: LambdaBinding[]) =>
    request<LambdaBinding[]>(`/api/lambdas/${key}/bindings`, {
      method: 'PUT',
      body: JSON.stringify({ bindings }),
    }),

  listSnowflakeProfiles: () => request<SnowflakeProfile[]>('/api/snowflake-profiles'),
  putSnowflakeProfile: (key: string, body: SnowflakeProfile) =>
    request<SnowflakeProfile>(`/api/snowflake-profiles/${key}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  validateEstate: () => request<ValidationResult>('/api/validate', { method: 'POST' }),
  publish: (note: string) =>
    request<Publication>('/api/publish', {
      method: 'POST',
      body: JSON.stringify({ note, allow_warnings: true }),
    }),
  publications: () => request<Publication[]>('/api/publications'),
  snapshotPreview: () => request<unknown>('/api/snapshot/preview'),

  resolve: (value: string, by: 'parent_company' | 'bucket' | 'alias') =>
    request<{
      resolved: boolean;
      input: string;
      parent_key: string | null;
      display_name?: string;
      explanation?: string;
    }>('/api/resolve', { method: 'POST', body: JSON.stringify({ value, by }) }),

  listUsers: () => request<AdminUserRow[]>('/api/users'),
  createUser: (body: {
    email: string;
    password: string;
    display_name?: string;
    role?: 'admin' | 'viewer';
    confirm_mfa?: boolean;
  }) =>
    request<{
      email: string;
      role: string;
      mfa_enrolled: boolean;
      provisioning_uri: string;
      totp_secret: string;
    }>('/api/users', { method: 'POST', body: JSON.stringify(body) }),
  patchUser: (
    email: string,
    body: {
      display_name?: string;
      role?: 'admin' | 'viewer';
      active?: boolean;
      password?: string;
      unlock?: boolean;
    },
  ) => request<AdminUserRow>(`/api/users/${encodeURIComponent(email)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  }),
  confirmMfa: (email: string) =>
    request<{ email: string; mfa_enrolled: boolean }>(
      `/api/users/${encodeURIComponent(email)}/confirm-mfa`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  listAudit: (month: string) => request<AuditEntry[]>(`/api/audit/${month}`),
};

export function emptyBinding(parentKey: string, lambdaKey: string): LambdaBinding {
  return {
    parent_key: parentKey,
    lambda_key: lambdaKey,
    enabled: false,
    file_type_keys: [],
    eventbridge_enabled: false,
    eventbridge_prefix: null,
    filename_stem_override: null,
    bucket_override: null,
    zoho_partner_ids: [],
    snowflake_profile_key: null,
    webhook_enabled: false,
    integration_key: null,
    extra: {},
    notes: '',
    updated_at: '',
  };
}
